
import { Component, HostListener, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { ActivatedRoute, Router } from '@angular/router';
import { Utils } from '../../../Utils/utils';
import { customValidator } from '../../../Utils/validator';
import { ApiService } from '../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { PaymentResponseList } from './payment.model';
import { SkeletonLoaderComponent } from "../../../shared/components/skeleton-loader/skeleton-loader.component";
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { ServiceName, ServiceNameEn } from '../../../shared/constants/services.enum';
import { VenueBookingQueryRequest } from '../../../shared/models/services-request/venue-booking-query.model';
import { SharedService } from '../../../shared/services/shared.service';
import { environment } from '../../../../environments/environment';
import { SrStatus } from '../../../shared/constants/status.enum';
import { VenueTokenService } from '../../../core/services/venue-token.service';
import { catchError, firstValueFrom, map, of, switchMap } from 'rxjs';
import { SrStatusRequest } from '../../../shared/models/services-request/sr-status-request.model';

interface VenueAmountGroup {
  label: string;
  amount: number;
}

interface VenuePaymentSummary {
  paymentDetails: VenueAmountGroup[];
  payableAmount: VenueAmountGroup[];
}

interface VenueSummaryCard {
  roomName: string;
  amenities: string;
  seats: string;
  accessibility: string;
  dateText: string;
  timeText: string;
  imageUrl: string;
}

@Component({
  standalone: true,
  selector: 'app-payment',
  imports: [ReactiveFormsModule, TranslateModule, DatePipe, DecimalPipe, ...SHARED_COMPONENTS, SkeletonLoaderComponent],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent implements OnInit {
  private readonly venueImageHostUrl = environment.venueImageHostUrl;
  lookupKey = LookupKey;
  service = ServiceName
  form: FormGroup;
  srNumber: any = "";
  csn: string = "";
  isMultiplePayment: boolean = false;

  // This method is used to initialize the class instance using fb, router, api, and route
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private api: ApiService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private venueTokenService: VenueTokenService
  ) {
    // SR Number in the url
    this.route.params.subscribe(params => {
      this.srNumber = params?.['sr_no'];

      this.route.queryParams.subscribe(query => {
        if (query?.['csn']) {
          this.csn = String(query?.['csn']).trim();
        }
        const payFor = query['payfor'];
        // If SR in URL or payFor in query
        if (this.srNumber || payFor != null) {
          if (payFor) {
            this.isMultiplePayment = true;
            this.srNumber = payFor; // e.g. "SR123,SR456"
          }
        } else {
          // nothing → go back to dashboard
          this.router.navigate([RoutesPath.DASHBOARD]);
        }
      });
    });

    this.form = this.fb.group({
      payMethod: ['', Validators.required],
      cardNumber: [''],
      expMonth: [null],
      expYear: [null],
      cardName: [null],
      cvv: [null]
    });
    this.form.valueChanges.subscribe(() => this.applyValidation());
  }

  months = Array.from({ length: 12 }, (_, i) => ({
    label: `${i + 1}`.padStart(2, '0'),
    value: `${i + 1}`.padStart(2, '0')
  }));

  years = Array.from({ length: 15 }, (_, i) => {
    const y = new Date().getFullYear() + i;
    return { label: `${y}`, value: `${y}` };
  });

  // This method is used to by card
  get byCard() {
    return this.form.get('payMethod')!.value === 'card';
  }

  // This method is used to handle the on init lifecycle hook
  ngOnInit() {
    this.redirectingToPayment = false;
    this.form.valueChanges.subscribe(() => this.applyValidation());

    this.getSrPaymentDetail();
  }

  @HostListener('window:pageshow', ['$event'])
  onWindowPageShow(event: PageTransitionEvent): void {
    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isBackForwardNavigation = navigationEntries.some(
      (entry: PerformanceNavigationTiming) => entry.type === 'back_forward'
    );

    if (!event.persisted && !isBackForwardNavigation) {
      return;
    }

    if (this.redirectingToPayment) {
      this.redirectingToPayment = false;
      this.technicalErrorMsg = false;
      this.getSrPaymentDetail();
    }
  }

  paymentList: PaymentResponseList = [];
  paymentLoader: boolean = false;
  serviceType: string = "";
  subServiceType: string = "";
  grandTotal: any = 0;
  isVenueBookingService: boolean = false;
  venuePaymentSummary: VenuePaymentSummary | null = null;
  venueSummaryCard: VenueSummaryCard | null = null;
  venueRoomPrice: number = 0;
  venueBookingId: string = '';
  venueSrStatus: string = '';

  // This method is used to get sr payment detail
  getSrPaymentDetail() {
    if (!this.srNumber) {
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }
    this.paymentLoader = true;
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Service Fees Line WF",
        "SRNumber": this.srNumber //"1-12688232998"//Multitple: "1-12687654729,1-12687654829"
      }
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_SR_SERVICE_FEES,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if (response['Error Code'] == null && response["SiebelMessage"]["DC Service Request Thin"]) {
          this.paymentList = Utils.checkIsArray(response["SiebelMessage"]["DC Service Request Thin"]);
          if (this.paymentList) {
            this.serviceType = this.paymentList[0]?.["SR Type"];
            this.subServiceType = this.paymentList[0]?.["SR Sub Type"];

            if (this.checkIsVenueBooking(this.serviceType, this.subServiceType)) {
              const srNumbers = this.srNumber.split(',').map((sr: any) => sr.trim()).filter(Boolean);
              if (srNumbers.length > 1) {
                this.isVenueBookingService = false;
              } else {
                this.isVenueBookingService = true;
              }
            }

            if (this.isVenueBookingService) {
              this.getVenueSrDetail();
            } else {
              this.paymentLoader = false;
            }
            this.venuePaymentSummary = this.isVenueBookingService
              ? this.buildVenuePaymentSummary(this.paymentList, this.venueRoomPrice)
              : null;
          }
          this.grandTotal = this.getGrandTotal(response);
        } else {
          this.technicalErrorMsg = true;
          this.paymentLoader = false;
        }
      },
      error: () => {
        this.paymentLoader = false;
        this.technicalErrorMsg = true
      }
    })
  }

  totalAmount(payments: any): number {
    return (payments || [])
      .reduce((sum: number, row: any) => sum + (+row['Amount'] || 0), 0);
  }
  getGrandTotal(response: any): number {
    const serviceRequests =
      response?.SiebelMessage?.['DC Service Request Thin'] || [];

    return serviceRequests.reduce((srTotal: number, sr: any) => {
      const charges = sr['DC FS Charge'] || [];
      const chargeTotal = charges.reduce(
        (sum: number, c: any) => sum + (+c['Amount'] || 0),
        0
      );
      return srTotal + chargeTotal;
    }, 0);
  }

  private roundAmount(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  get hasVenueSummaryCard(): boolean {
    return !!this.venueSummaryCard && this.isVenueBookingService;
  }

  get payableTotalAmount(): number {
    if (this.isVenueBookingService && this.venuePaymentSummary?.payableAmount?.length) {
      const payableTotal = this.venuePaymentSummary.payableAmount.find((item: VenueAmountGroup) => item.label === 'Total Fees');
      if (payableTotal) {
        return Number(payableTotal.amount) || 0;
      }
    }
    return Number(this.grandTotal) || 0;
  }

  get hasZeroPayableAmount(): boolean {
    return this.payableTotalAmount <= 0;
  }



  // This method is used to apply validation
  private applyValidation() {
    if (this.byCard) {
      this.toggleRequired(this.form, true, [
        'cardNumber', 'expMonth', 'expYear', 'cardName', 'cvv'
      ]);
    } else {
      this.toggleRequired(this.form, false, [
        'cardNumber', 'expMonth', 'expYear', 'cardName', 'cvv'
      ]);
    }
  }

  // This method is used to toggle required using form, condition, and controls
  private toggleRequired(form: FormGroup, condition: boolean, controls: string[]): void {
    controls.forEach((key) => {
      const ctl = form.get(key)!;
      if (condition) {
        const validators = [Validators.required];
        if (key == 'cardNumber') {
          validators.push(customValidator('cardNumber'));
        }
        if (key == 'cvv') {
          validators.push(customValidator('cvv'));
        }
        ctl.addValidators(validators);
      } else {
        ctl.clearValidators();
      }
      ctl.updateValueAndValidity({ emitEvent: false });
    });
  }

  redirectingToPayment: boolean = false;
  technicalErrorMsg: boolean = false;
  // This method is used to pay
  async pay(): Promise<void> {
    this.technicalErrorMsg = false;
    if( this.paymentList[0]?.["DC Online Pay Flag"] === 'Y') {
      this.sharedService.toastMessage('INFO', 'ONLINE_PAYMENT_NOTALLOWED', 'info', true); 
      return;
    }
    if (this.form.valid) {
      this.redirectingToPayment = true;
      let formData = this.form.getRawValue();
      let requestPayload = {
        "PaymentType": formData.payMethod,
        "Process_spcInstance_spcId": "",
        "Object_spcId": "",
        "Siebel_spcOperation_spcObject_spcId": "",
        "SR_spcNumber": this.srNumber,
        "Amount": "",
        "Transaction_spcId": "",
        "Error_spcCode": "",
        "PaymentURL": "",
        "redirectionurl": this.buildPaymentConfirmationRedirectUrl(),
        "Error_spcMessage": "432432"
      }

      if (this.isVenueBookingService) {
        const isValidForPayment = await this.validateVenueBookingForPayment();
        if (!isValidForPayment) {
          this.redirectingToPayment = false;
          return;
        }
      }
      this.api.post(ApiRoot.DCCIGenEPay, ApiEndPoints.ePAY_GENERATE, requestPayload)
        .subscribe({
          next: (response: any) => {
            if (response?.PaymentURL && response?.PaymentURL != null) {
              let paymentURL = response.PaymentURL;
              window.location.href = paymentURL;
            } else {
              this.redirectingToPayment = false;
              this.technicalErrorMsg = true;
            }
          },
          error: () => {
            this.redirectingToPayment = false;
            this.technicalErrorMsg = true;
          }
        })
    } else {
      const invalidControls = Utils.getInvalidControls(this.form);
      console.warn('Invalid controls:', invalidControls);
      this.form.markAllAsTouched();
    }
  }

  //========================VENUE BOOKING =============

  private checkIsVenueBooking(srType: string, srSubType: string): boolean {
    const values = [srType, srSubType]
      .map((value: string) => String(value ?? '').trim().toLowerCase())
      .filter((value: string) => !!value);

    if (!values.length) {
      return false;
    }

    return values.some((value: string) =>
      value.includes(ServiceNameEn.VENUE_BOOKING.toLowerCase())
      || value.includes(String(this.service.VENUE_BOOKING).toLowerCase())
    );
  }

  private buildVenuePaymentSummary(paymentList: PaymentResponseList, roomPriceFromSrDetail: number = 0): VenuePaymentSummary {
    let securityDeposit = 0;

    Utils.checkIsArray(paymentList).forEach((payment: any) => {
      Utils.checkIsArray(payment?.['DC FS Charge']).forEach((charge: any) => {
        const productName = String(charge?.['Product Name'] ?? '').toLowerCase();
        const amount = Number(charge?.['Amount'] ?? 0);

        if (productName.includes('security deposit')) {
          securityDeposit += amount;
        }
      });
    });

    const roomFee = roomPriceFromSrDetail > 0
      ? roomPriceFromSrDetail
      : 0;
    //(securityDeposit > 0 ? securityDeposit * 10 : 0);
    const paymentVat = roomFee * 0.05;
    const paymentTotal = roomFee + securityDeposit + paymentVat;

    const isFinalPayment = this.venueSrStatus === SrStatus.AwaitingFinalPayment;
    const payableBaseAmount = isFinalPayment
      ? roomFee * 0.90
      : roomFee * 0.10;
    const payableSecurityDeposit = isFinalPayment ? securityDeposit : 0;
    const payableVat = payableBaseAmount * 0.05;
    const payableTotal = payableBaseAmount + payableSecurityDeposit + payableVat;
    const payableLabel = isFinalPayment
      ? 'Final Booking Payment (90%)'
      : 'Booking Fees (10%) (Non-refundable)';

    return {
      paymentDetails: [
        { label: 'Room Fee', amount: this.roundAmount(roomFee) },
        { label: 'Security Deposit (10%) (Refundable)', amount: this.roundAmount(securityDeposit) },
        { label: 'VAT (5%)', amount: this.roundAmount(paymentVat) },
        { label: 'Total Fees', amount: this.roundAmount(paymentTotal) }
      ],
      payableAmount: [
        { label: payableLabel, amount: this.roundAmount(payableBaseAmount) },
        ...(isFinalPayment
          ? [{ label: 'Security Deposit (10%) (Refundable)', amount: this.roundAmount(payableSecurityDeposit) }]
          : []),
        { label: 'VAT (5%)', amount: this.roundAmount(payableVat) },
        { label: 'Total Fees', amount: this.roundAmount(payableTotal) }
      ]
    };
  }

  // This is method is used to get venue booking sr detail
  private getVenueSrDetail(): void {
    if (!this.srNumber) {
      return;
    }
    const userProfile = this.sharedService.getUserProfile() || {};
    const request = new VenueBookingQueryRequest();
    request.body.DCLoginName = String(userProfile?.['Login Name'] ?? '').trim();
    request.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    const sr = request.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR'];
    sr['SR Number'] = this.srNumber;
    sr['SR Type'] = ServiceName.VENUE_BOOKING;
    sr['Sub Type'] = ServiceName.VENUE_BOOKING;
    sr.CSN = this.csn ? this.csn : this.sharedService.getCsnAccountId('csn') || '';

    this.venueTokenService.ensureVenueToken().pipe(
      switchMap(() =>
        this.api.post(
          ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
          ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
          request
        )
      )
    ).subscribe({
      next: (response: any) => {
        const srDetail = response?.SiebelMessage?.['DC Community Portal SR'];
        if (srDetail) {
          this.venueSrStatus = String(srDetail?.['Status'] ?? '').trim();
          this.venueRoomPrice = this.parseAmount(srDetail?.['DC Venue Booking RoomPrice']);
          this.venueBookingId = String(srDetail?.['DC Venue Booking Id'] ?? '').trim();
          this.venueSummaryCard = this.mapVenueSummaryCard(srDetail);

          const isPastBookingDate = this.isBeforeTodayDateOnly(srDetail?.['DC Venue Booking End Time']);
          if ((srDetail["Status"] == SrStatus.Draft || srDetail["Status"] == SrStatus.ApprovedAndPaymentAwaited) && isPastBookingDate) {
            this.updateRequestStatus();
            this.sharedService.toastMessage('ERROR', 'VENUE_BOOKING_NOT_FOUND', 'error');
            this.router.navigate([`${RoutesPath.APPLICATIONS}`]);
            return;
          }

          this.validateVenueBookingForPayment();
          if (this.paymentList?.length) {
            this.venuePaymentSummary = this.buildVenuePaymentSummary(this.paymentList, this.venueRoomPrice);
          }
        }
      },
      error: () => {
        this.paymentLoader = false;
      }
    });
  }

  private validateVenueBookingForPayment(): Promise<boolean> {
    if (!this.venueBookingId) {
      return Promise.resolve(true);
    }

    return firstValueFrom(
      this.api.getVenueBookingDetail<any>(this.venueBookingId, 'actions').pipe(
        map((response: any) => {
          if (response?.status && response?.status == 'NOT_FOUND') {
            this.updateRequestStatus();
            this.redirectingToPayment = false;
            this.sharedService.toastMessage('Error', 'VENUE_BOOKING_NOT_FOUND', 'error');
            setTimeout(() => {
              this.paymentLoader = false;
              this.router.navigate([RoutesPath.DASHBOARD]);
            }, 3000);
            return false;
          }

          this.paymentLoader = false;
          return true;
        }),
        catchError(() => {
          this.redirectingToPayment = false;
          this.paymentLoader = false;
          this.technicalErrorMsg = true;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
          return of(false);
        })
      )
    );
  }

  private mapVenueSummaryCard(srDetail: any): VenueSummaryCard {
    const roomName = String(srDetail?.['DC Venue Booking Room'] ?? '').trim();
    const seatsRaw = String(srDetail?.['DC Venue Booking Attendees'] ?? '').trim();

    const amenities = Utils.checkIsArray(srDetail?.['DC SR Venue Booking Amenities BC'])
      .map((item: any) => String(item?.['DC Amenities'] ?? '').trim())
      .filter((item: string) => !!item)
      .join(', ');

    const imageUrl = this.extractVenueImageUrl(srDetail);
    return {
      roomName: roomName || '-',
      amenities: amenities || '-',
      seats: seatsRaw || '-',
      accessibility: 'Accessible for everyone',
      dateText: srDetail?.['DC Venue Booking Start Time'],
      timeText: '', //this.formatVenueDate(startDate),     
      imageUrl
    };
  }

  private extractVenueImageUrl(srDetail: any): string {
    const directList = Utils.checkIsArray(srDetail?.['DC SR Venue Booking Room Images BC']);
    const nestedList = Utils.checkIsArray(
      srDetail?.['ListOfDC SR Venue Booking Room Images BC']?.['DC SR Venue Booking Room Images BC']
    );
    const source = directList.length ? directList : nestedList;
    const rawUrl = String(source?.[0]?.['DC Room Image'] ?? '').trim();

    if (!rawUrl) {
      return '';
    }
    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }
    const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    return `${this.venueImageHostUrl}${normalizedPath}`;
  }

  private parseAmount(value: any): number {
    const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private buildPaymentConfirmationRedirectUrl(): string {
    const baseUrl = `${window.location.origin}/service/payment-confirmation`;
    const params = new URLSearchParams();
    params.set('sr', String(this.srNumber ?? ''));

    if (this.isVenueBookingService && this.venueBookingId) {
      params.set('venueBookingId', this.venueBookingId);
    }

    return `${baseUrl}?${params.toString()}`;
  }
  // End

  private isBeforeTodayDateOnly(value: any): boolean {
    const bookingDate = value ? new Date(value) : null;
    if (!bookingDate) {
      return false;
    }
    const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
    const today = new Date();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return bookingDay < todayDay;
  }

  // This method is use to update sr status as rejected in siebel when the booking is not found in the venue booking system
    private updateRequestStatus(): void {
      const userProfile = this.sharedService.getUserProfile() || {};
      const requestPayload = new SrStatusRequest();
      requestPayload.body.DCLoginName = String(userProfile?.['Login Name'] ?? '').trim();
      requestPayload.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
      requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
  
      const sr = requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR Upsert IO']['DC Community Portal SR'];
      sr['SR Number'] = this.srNumber;
      sr.Status = SrStatus.Rejected;
  
      this.api.postWithoutRetry(
        ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
        ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
        requestPayload
      ).subscribe({
        next: (response: any) => {
          if (response?.['Error Code'] == null) {
          }
        }
      });
    }
}
